import {
  LinkClickTrackingPlugin,
  enableLinkClickTracking,
  enableButtonClickTracking
} from '@convivainc/conviva-js-appanalytics-click-tracking';
import {
  ErrorTrackingPlugin,
  enableErrorTracking
} from '@convivainc/conviva-js-appanalytics-error-tracking';
import { PerformanceTimingPlugin } from '@convivainc/conviva-js-appanalytics-performance-timing';
import dynamic from 'next/dynamic';

import {
  getLocalStorage,
  getSessionStorage
} from '../../browserStorageHandler';
import {
  getUTMParamsAsObject,
  getDeviceResolution,
  getDeviceAudioTechnology,
  getDeviceVideoCodec,
  getDeviceVideoRange,
  languageMapping,
  getSelectedSubtitle,
  getVideoQuality,
  isSubtitlesAvailable,
  drmSecurityLevel,
  getUsersAge,
  getDevice
} from '../../common';
import { PLAN_ID } from '../../constants/cookieConstants';
import {
  USER_TYPE,
  USER_ID,
  GUEST_TOKEN,
  PARTNER_NAME,
  PARTNER_SOURCE
} from '../../constants/localStorageConstants';
import {
  convivaCustomConstants,
  LIVE_EVENT,
  SPORTS_VOD
} from '../../constants/utilConstants';

// eslint-disable-next-line func-names
const ConvivaAnalyticHelper = function () {
  const CONVIVA_CUSTOMER_KEY = process.env.CONVIVA_CUSTOMER_KEY;
  const TOUCHSTONE_SERVICE_URL = process.env.TOUCHSTONE_SERVICE_URL;
  const CONVIVA_SETTINGS = process.env.CONVIVA_SETTINGS;
  let convivaSDK: any;
  let convivaAdAnalytics: any = null;

  const isImadai = () => window.is_dai || false;
  const getDeviceType = () => {
    try {
      if (window && window.navigator && window.Conviva) {
        if (
          window.navigator.userAgent.match(/iPhone|iPod|Android/i) &&
          window.innerWidth <= 500
        ) {
          return window.Conviva.Constants.DeviceType.MOBILE;
        }
        if (
          window.navigator.userAgent.match(/Tablet|iPad/i) &&
          window.innerWidth <= 1280 &&
          window.innerHeight >= 800 &&
          window.innerWidth > 500
        )
          return window.Conviva.Constants.DeviceType.TABLET;
        if (window.navigator.userAgent.toLowerCase().match(/tv/i))
          return window.Conviva.Constants.DeviceType.SMARTTV;
        if (window.innerWidth > 1000 && window.innerHeight > 700)
          return window.Conviva.Constants.DeviceType.DESKTOP;
      }
    } catch (error) {
      console.error('Error in window.navigator: ', error);
    }
    return convivaCustomConstants.NOT_APPLICABLE;
  };

  const getResoulution = () => {
    return (
      (window &&
        window.screen &&
        window.screen.height &&
        window.screen.height.toString()) ||
      'NA'
    );
  };

  const getPlatformName = () => {
    return convivaCustomConstants.WEB;
  };
  const getDeviceCategory = () => {
    if (window && window.Conviva) {
      return window.Conviva.Constants.DeviceCategory.WEB;
    }
    return convivaCustomConstants.NOT_APPLICABLE;
  };

  const isCatchup = (assetInfo: any) => {
    if (assetInfo && Object.keys(assetInfo).length) {
      const tvshowInfo = assetInfo.tvshow || assetInfo.tvshow_details;
      if (
        assetInfo.business_type.indexOf('premium') > -1 &&
        tvshowInfo &&
        tvshowInfo.asset_subtype === 'tvshow'
      ) {
        return 'N';
      }
      if (assetInfo.asset_subtype === 'episode') {
        return 'Y';
      }
      return 'N';
    }
    return 'N';
  };

  const getMetaData = () => {
    const parser = window?.navigator?.userAgent;
    const deviceData = getDevice(parser);
    const deviceMetadata = {} as any;
    deviceMetadata[window.Conviva.Constants.DeviceMetadata.TYPE] =
      getDeviceType();
    deviceMetadata['m3.dv.hwt'] = getDeviceType();
    deviceMetadata['m3.dv.cat'] = getDeviceCategory();
    deviceMetadata[window.Conviva.Constants.DeviceMetadata.CATEGORY] =
      getDeviceCategory();
    // deviceMetadata[ window.Conviva.Constants.DeviceMetadata.OS_NAME ] = getOS();
    // deviceMetadata[ window.Conviva.Constants.DeviceMetadata.OS_VERSION ] = getOSVersion();
    // deviceMetadata[ "m3.dv.osf" ] = getOS()
    // deviceMetadata[ "m3.dv.os" ] = getOS()
    // deviceMetadata[ "m3.dv.osv" ] = getOSVersion();
    // const browserInfo = getBrowserName();
    // deviceMetadata[ "m3.dv.br" ] = browserInfo.browserName;
    // deviceMetadata[ "m3.dv.brv" ] = browserInfo.browserVersion;
    deviceMetadata['m3.dv.mnf'] = deviceData.VENDOR || 'NA'; // need to check the device manufacture
    // deviceMetadata[ "m3.dv.mrk" ] = "NA" // need to check the device Marketing Name
    deviceMetadata['m3.dv.n'] = deviceData.MODEL || 'NA'; // need to check the device name
    return deviceMetadata;
  };

  //TODO: Revisit to check connection type @anivnash
  const getConnectionType = () => {
    let effectivetype = convivaCustomConstants.WIFI;
    try {
      const navigator = window.navigator as any;
      if (window && navigator.connection) {
        if (
          navigator.connection.type &&
          navigator.connection.type.toLowerCase() !== 'cellular'
        ) {
          effectivetype = navigator.connection.type;
        }
        effectivetype = navigator.connection.effectiveType;
        if (effectivetype === convivaCustomConstants.DEFAULT_EFFECTIVE_TYPE) {
          effectivetype = convivaCustomConstants.WIFI;
        }
      }
    } catch (error) {
      console.error('error in getting effective type: ', error);
    }
    return effectivetype;
  };

  const getCategoryTitle = (assetInfo: any) => {
    const {
      tvshow_name = 'NA',
      asset_type = 'NA',
      original_title = 'NA'
    } = assetInfo;
    try {
      if (asset_type === 1) {
        return tvshow_name;
      }
      return original_title;
    } catch (error) {
      return convivaCustomConstants.NOT_APPLICABLE;
    }
  };

  const getContentTypeTitle = (assetInfo: any) => {
    const {
      release_date = 'NA',
      asset_type = 'NA',
      orderid = '0',
      original_title = 'NA',
      genres: [genreDetail] = []
    } = assetInfo;
    try {
      const time =
        release_date &&
        release_date.split('T')[0].split('-').reverse().join('');
      if (asset_type === 1) {
        const category =
          genreDetail &&
          genreDetail.id &&
          genreDetail.id.toLowerCase() === 'sports'
            ? 'Match'
            : 'E';
        return time
          .concat('-')
          .concat(category)
          .concat(orderid)
          .concat('-')
          .concat(original_title);
      }
      return original_title;
    } catch (error) {
      return original_title;
    }
  };

  const utmTrackingUrl = () => {
    const getJson = getUTMParamsAsObject(window.location.search);
    if (getJson && 'utm_source' in getJson) {
      return window.location.href;
    }
    return convivaCustomConstants.ORGANIC;
  };

  const getAffiliate = () => {
    let partnerSource = getSessionStorage(PARTNER_NAME);
    if (partnerSource && typeof partnerSource !== 'undefined') {
      return partnerSource;
    }
    partnerSource = getLocalStorage(PARTNER_NAME);
    if (partnerSource && typeof partnerSource !== 'undefined') {
      return partnerSource;
    }
    return convivaCustomConstants.DEFAULT_PARTNER_ZEE5;
  };

  const getPlanId = () => {
    const planId = getSessionStorage(PLAN_ID);
    if (planId && typeof planId !== 'undefined') {
      return planId;
    }
    return planId || convivaCustomConstants.NOT_APPLICABLE;
  };

  const getContentCategoryType = (categoryInfo: string) => {
    let contentCategoryType = '';
    switch (categoryInfo) {
      case SPORTS_VOD:
        contentCategoryType = convivaCustomConstants.VOD;
        break;
      case LIVE_EVENT:
        contentCategoryType = convivaCustomConstants.LIVE;
        break;
      default:
        contentCategoryType = categoryInfo;
        break;
    }
    return contentCategoryType;
  };

  const getCategoryType = (categoryInfo: string) => {
    let categoryType = '';
    switch (categoryInfo) {
      case SPORTS_VOD:
        categoryType = 'Sports_VOD';
        break;
      case LIVE_EVENT:
        categoryType = 'Live_Event';
        break;
      default:
        categoryType = categoryInfo;
        break;
    }
    return categoryType;
  };

  const getAccessType = () => {
    const accessType = {
      guest: convivaCustomConstants.GUEST,
      register: convivaCustomConstants.AUTHENTICATED,
      premium: convivaCustomConstants.PREMIUM
    };
    try {
      const userTypeValue = getLocalStorage(USER_TYPE) as string;
      if (userTypeValue) {
        return accessType[userTypeValue as keyof typeof accessType];
      }
    } catch (error) {
      return convivaCustomConstants.GUEST;
    }
    return convivaCustomConstants.GUEST;
  };

  const getContentInfo = (assetInfo = {} as any, userData = {} as any) => {
    const {
      video_url: { mpd, m3u8 } = { mpd: '', m3u8: '' },
      hls_token: hlsToken,
      id,
      is_drm: isDrm,
      video_token: videoToken,
      showDetails = {},
      asset_subtype: assetSubtype = '',
      tvshow_name: showName = convivaCustomConstants.NOT_RECEIVED,
      content_partner_details: partnerDetails
    } = assetInfo as any;
    const parser = window?.navigator?.userAgent;
    const deviceData = getDevice(parser);
    let { asset_subtype: categoryInfo = assetSubtype } = showDetails || {};
    if (!categoryInfo) {
      categoryInfo = convivaCustomConstants.NOT_APPLICABLE;
    }
    const streamUrl = isDrm ? mpd : m3u8 || hlsToken || videoToken;
    if (assetInfo && typeof assetInfo === 'object') {
      const IsLive =
        (assetInfo.id &&
          assetInfo.id.split('-').length &&
          assetInfo.id.split('-')[1] === '9') ||
        (assetInfo.slug && assetInfo.slug.indexOf('0-9-') > -1);
      const contentMetadata = {} as any;
      contentMetadata.accessType =
        getAccessType() || convivaCustomConstants.GUEST;
      contentMetadata.userAge =
        getUsersAge(userData?.birthday) || convivaCustomConstants.NOT_RECEIVED;
      contentMetadata.userGender =
        userData?.gender || convivaCustomConstants.NOT_RECEIVED;
      contentMetadata['c3.cm.aggregatorPartner'] =
        partnerDetails?.content_partner_name || 'Zee5';
      contentMetadata.aggregatorPartner =
        partnerDetails?.content_partner_name || convivaCustomConstants.ZEE5;
      contentMetadata['c3.cm.affiliate'] = getAffiliate();
      contentMetadata['c3.cm.planId'] = getPlanId();
      contentMetadata.clickSource = convivaCustomConstants.CLICK_SOURCE;
      const partnerSource =
        getSessionStorage(PARTNER_SOURCE) ||
        getAffiliate() ||
        convivaCustomConstants.NOT_APPLICABLE;
      contentMetadata.CategoryTitle = getCategoryTitle(assetInfo);
      contentMetadata.ContentTypeTitle = getContentTypeTitle(assetInfo);
      contentMetadata.playbackspeed = '1x';
      contentMetadata.primaryLanguage =
        assetInfo.languages && assetInfo.languages.length
          ? assetInfo.languages[0]
          : convivaCustomConstants.NOT_APPLICABLE;
      contentMetadata.partnerSource = partnerSource;
      contentMetadata['c3.cm.utmTrackingUrl'] = utmTrackingUrl();
      contentMetadata.playerName = getPlatformName();
      contentMetadata.subtitleLanguage = getSelectedSubtitle();
      contentMetadata.videoCueStart = '00.00.00';
      contentMetadata.videoCueEnd = '00.00.00';
      contentMetadata.videoPlaybackResolution = getVideoQuality();
      contentMetadata['c3.app.version'] =
        process.env.APP_VERSION || convivaCustomConstants.NOT_APPLICABLE;
      contentMetadata.drmSecLevel = drmSecurityLevel();
      contentMetadata['c3.cm.id'] = id || convivaCustomConstants.NOT_APPLICABLE;
      contentMetadata['c3.cm.brand'] =
        convivaCustomConstants.ZEE5 || convivaCustomConstants.NOT_APPLICABLE;
      contentMetadata.contentId =
        assetInfo.id || convivaCustomConstants.NOT_APPLICABLE;
      contentMetadata['c3.cm.categoryType'] = IsLive
        ? convivaCustomConstants.LIVE
        : getCategoryType(categoryInfo);
      contentMetadata.contentCategory = IsLive
        ? convivaCustomConstants.LIVE
        : getContentCategoryType(categoryInfo);
      contentMetadata.contentSubCategory = getContentCategoryType(categoryInfo);
      contentMetadata.category = getCategoryType(categoryInfo);
      contentMetadata.playbackType =
        getLocalStorage('languageChangeTriggered') === 'true'
          ? 'Language Change'
          : convivaCustomConstants.MANUAL;
      contentMetadata.show = `${showName}-${convivaCustomConstants.SEASON1}`;
      contentMetadata.contentType =
        contentMetadata.category || convivaCustomConstants.NOT_APPLICABLE;
      contentMetadata['c3.cm.contentType'] =
        contentMetadata.contentCategory ||
        convivaCustomConstants.NOT_APPLICABLE;
      contentMetadata['c3.video.isLive'] = IsLive
        ? convivaCustomConstants.LIVE
        : getContentCategoryType(categoryInfo);
      const genres = assetInfo.genre || assetInfo.genres;
      contentMetadata['c3.cm.genre'] =
        genres && genres.length
          ? genres
              .map(
                (channelObj: { id: any; value: any }) =>
                  channelObj.id || channelObj.value
              )
              .join(', ')
          : convivaCustomConstants.NOT_APPLICABLE;
      contentMetadata[
        window.Conviva.Constants.ASSET_NAME
      ] = `${assetInfo.id} - ${assetInfo.original_title}`;
      contentMetadata.contentCategoryTitle = assetInfo.tvshow_name
        ? assetInfo.tvshow_name
        : convivaCustomConstants.NOT_APPLICABLE;
      contentMetadata.contentSubCategoryTitle = assetInfo.tvshow_name
        ? assetInfo.tvshow_name
        : convivaCustomConstants.NOT_APPLICABLE;
      contentMetadata['c3.cm.channel'] = IsLive
        ? `LiveTV ${assetInfo.original_title}`
        : convivaCustomConstants.NOT_APPLICABLE;
      contentMetadata['c3.cm.seriesName'] =
        assetInfo.original_title || convivaCustomConstants.NOT_APPLICABLE;
      contentMetadata['c3.cm.seasonNumber'] =
        assetInfo.season || convivaCustomConstants.NOT_APPLICABLE;
      contentMetadata['c3.cm.showTitle'] =
        assetInfo.original_title ||
        assetInfo.title ||
        convivaCustomConstants.NOT_APPLICABLE;
      contentMetadata['c3.cm.episodeNumber'] =
        (assetInfo.orderid && assetInfo.orderid.toString()) ||
        convivaCustomConstants.NOT_APPLICABLE;
      contentMetadata.episodeName =
        assetInfo.asset_subtype === 'episode'
          ? assetInfo.original_title || assetInfo.title
          : convivaCustomConstants.NOT_APPLICABLE;
      contentMetadata['c3.cm.name'] =
        (assetInfo.content_owner && assetInfo.content_owner.toString()) ||
        convivaCustomConstants.NOT_APPLICABLE;
      contentMetadata.audioLanguage =
        assetInfo.audio_languages && assetInfo.audio_languages.length
          ? languageMapping(assetInfo.audio_languages[0])
          : convivaCustomConstants.NOT_APPLICABLE;
      contentMetadata.originalLanguage =
        assetInfo.languages && assetInfo.languages.length
          ? assetInfo.languages[0]
          : convivaCustomConstants.NOT_APPLICABLE;
      contentMetadata.contentAccessType =
        (assetInfo.business_type && assetInfo.business_type) ||
        convivaCustomConstants.NOT_APPLICABLE;
      contentMetadata.contentLengthSec =
        (assetInfo.duration && assetInfo.duration * 1000).toString() ||
        convivaCustomConstants.NOT_APPLICABLE;
      contentMetadata.highestResolution =
        getResoulution() || convivaCustomConstants.NOT_APPLICABLE;
      contentMetadata.audioFormat = convivaCustomConstants.NOT_APPLICABLE;
      contentMetadata.connectionType =
        getConnectionType() || convivaCustomConstants.NOT_APPLICABLE;
      contentMetadata[window.Conviva.Constants.STREAM_URL] =
        streamUrl || convivaCustomConstants.NOT_APPLICABLE;
      contentMetadata.streamType =
        assetInfo && assetInfo.is_drm
          ? convivaCustomConstants.DRM
          : convivaCustomConstants.HLS;
      contentMetadata.streamingProtocol =
        assetInfo && assetInfo.is_drm
          ? convivaCustomConstants.DRM
          : convivaCustomConstants.HLS;
      contentMetadata['c3.csid'] = convivaCustomConstants.NOT_APPLICABLE;
      contentMetadata.rootID =
        assetInfo.id || convivaCustomConstants.NOT_APPLICABLE;
      contentMetadata.clickID =
        assetInfo.clickID || convivaCustomConstants.NOT_APPLICABLE;
      contentMetadata.clickID =
        assetInfo.clickID || convivaCustomConstants.NOT_APPLICABLE;
      contentMetadata.infoMessage = convivaCustomConstants.NOT_APPLICABLE;
      contentMetadata[window.Conviva.Constants.DURATION] =
        assetInfo.duration || convivaCustomConstants.NOT_APPLICABLE;
      contentMetadata.abExperiment = convivaCustomConstants.NO_EXPERIMENTS;
      contentMetadata.deviceResolution = getDeviceResolution();
      contentMetadata.deviceAudioTechnology = getDeviceAudioTechnology();
      contentMetadata.deviceVideoCodec = getDeviceVideoCodec();
      contentMetadata.deviceVideoRange = getDeviceVideoRange();
      contentMetadata['m3.dv.br'] =
        getMetaData()['m3.dv.br'] || convivaCustomConstants.NOT_APPLICABLE;
      contentMetadata['m3.dv.brv'] =
        getMetaData()['m3.dv.brv'] || convivaCustomConstants.NOT_APPLICABLE;
      contentMetadata.playerVersion = '3.3.0';
      contentMetadata.SenderAppName = 'Web-Zee5';
      contentMetadata.subtitle = isSubtitlesAvailable();
      contentMetadata.catchUp = isCatchup(assetInfo);
      contentMetadata.videoStartPoint = assetInfo.videoStartPoint || '00.00.00';
      const videoDuration = assetInfo.duration
        ? (assetInfo.duration / 60).toFixed(2)
        : 'NA';
      contentMetadata.videoEndPoint =
        assetInfo.videoEndPoint || videoDuration.toString();
      contentMetadata.playbackQuality = 'Auto';
      contentMetadata.autoPlay = assetInfo.autoPlay || 'false';
      contentMetadata.show = assetInfo.tvshow_name
        ? assetInfo.tvshow_name
        : 'NA';
      contentMetadata.season = assetInfo.season || 'NA';
      contentMetadata.channel = !IsLive
        ? (assetInfo.channels &&
            assetInfo.channels.length &&
            assetInfo.channels
              .map((data: any) => data.original_title)
              .join(', ')) ||
          convivaCustomConstants.NOT_APPLICABLE
        : assetInfo.original_title;
      contentMetadata.Subtitle = 'Off';
      contentMetadata.streamingProtocol =
        assetInfo && assetInfo.is_drm ? 'DRM' : 'HLS';
      contentMetadata.site =
        window && window.location && window.location.hostname;
      contentMetadata.appVersion = process.env.APP_VERSION || 'NA';
      contentMetadata.episodeNumber =
        (assetInfo.orderid && assetInfo.orderid.toString()) || 'NA';
      contentMetadata.origin = assetInfo.origin || 'NA';
      contentMetadata.modelName =
        assetInfo.modelName || convivaCustomConstants.NOT_APPLICABLE;
      contentMetadata.carrier = 'WiFi';
      console.log(contentMetadata, 'vineeth');
      contentMetadata['m3.dv.mnf'] = deviceData.VENDOR || 'NA';
      contentMetadata['m3.dv.n'] = deviceData.MODEL || 'NA';

      return contentMetadata;
    }
    return {};
  };

  return {
    convivaSessionStarted: false,
    convivaInitialized: false,
    getContentInfo,
    // eslint-disable-next-line
    initializeConviva: function (appAnalyticsFlag?: boolean) {
      if (appAnalyticsFlag) {
        import('@convivainc/conviva-js-appanalytics').then((conviva) => {
          const { convivaAppTracker, trackPageView, setUserId } = conviva;

          convivaAppTracker({
            appId: 'Web-Zee5',
            convivaCustomerKey: CONVIVA_CUSTOMER_KEY,
            contexts: {
              performanceTiming: true
            },
            plugins: [
              PerformanceTimingPlugin(),
              ErrorTrackingPlugin(),
              LinkClickTrackingPlugin()
            ]
          });
          setUserId(getLocalStorage(USER_ID) || getLocalStorage(GUEST_TOKEN));
          trackPageView();
          enableLinkClickTracking(); // Tracks all link clicks on the page
          enableButtonClickTracking();
          enableErrorTracking();
        });
      }
      if (window && window.Conviva && !this.convivaInitialized) {
        if (CONVIVA_SETTINGS === 'staging') {
          const settings = {} as any;
          settings[window.Conviva.Constants.GATEWAY_URL] =
            TOUCHSTONE_SERVICE_URL;
          settings[window.Conviva.Constants.LOG_LEVEL] =
            window.Conviva.Constants.LogLevel.ERROR;
          window.Conviva.Analytics.init(CONVIVA_CUSTOMER_KEY, null, settings);
        } else {
          window.Conviva.Analytics.init(CONVIVA_CUSTOMER_KEY, null);
        }
        convivaSDK = window.Conviva.Analytics.buildVideoAnalytics();
        window.Conviva.Analytics.setDeviceMetadata(getMetaData());
        // set connection type to wifi as constant, Recommended by conviva team
        window.Conviva.Analytics.reportDeviceMetric(
          window.Conviva.Constants.Network.CONNECTION_TYPE,
          getConnectionType()
        );
        this.convivaInitialized = true;
        window.convivaSDK = convivaSDK;
        convivaAdAnalytics =
          window.Conviva.Analytics.buildAdAnalytics(convivaSDK);
        window.convivaAdAnalytics = convivaAdAnalytics;
        convivaAdAnalytics.setAdPlayerInfo();
        return true;
      }
      return false;
    },
    // eslint-disable-next-line
    setContentInfo: function (data: { title: any }) {
      if (convivaSDK && this.convivaSessionStarted) {
        if (data.title) {
          convivaSDK.setContentInfo(getContentInfo(data));
        } else {
          convivaSDK.setContentInfo(data);
        }
      }
    },
    // eslint-disable-next-line
    setConvivaErrorInfo: function (data: any, severity: any) {
      if (convivaSDK && this.convivaSessionStarted) {
        const errorPayload =
          typeof data === 'string' || data instanceof String
            ? data
            : JSON.stringify(data);
        try {
          convivaSDK.reportPlaybackError(errorPayload, severity);
        } catch (error) {
          console.error('Exception in setConvivaErrorInfo  method: ', error);
        }
      }
    },

    setInitialContentInfo: function (
      assetInfo: {
        id: string;
        slug: string | string[];
        asset_subtype: string;
        aston_segments: any;
        video_segments: any;
      },
      userData: any = {}
    ) {
      const isConvivaSessionRequired =
        window && window.isConvivaSessionRequired;
      if (
        convivaSDK &&
        !this.convivaSessionStarted &&
        isConvivaSessionRequired
      ) {
        const IsLive =
          (assetInfo.id &&
            assetInfo.id.split('-').length &&
            assetInfo.id.split('-')[1] === '9') ||
          (assetInfo.slug && assetInfo.slug.indexOf('0-9-') > -1) ||
          assetInfo.asset_subtype === LIVE_EVENT;
        const contentMetadata = getContentInfo(assetInfo, userData);
        window.Conviva.Analytics.setDeviceMetadata(getMetaData());
        contentMetadata[window.Conviva.Constants.PLAYER_NAME] = 'Web-Zee5';
        // eslint-disable-next-line
        contentMetadata[window.Conviva.Constants.VIEWER_ID] =
          getLocalStorage(USER_ID) || getLocalStorage(GUEST_TOKEN);
        contentMetadata['c3.viewer.id'] =
          getLocalStorage(USER_ID) || getLocalStorage(GUEST_TOKEN);
        contentMetadata[window.Conviva.Constants.DEFAULT_RESOURCE] =
          convivaCustomConstants.DEFAULT_RESOURCE;
        contentMetadata[window.Conviva.Constants.IS_LIVE] = IsLive
          ? window.Conviva.Constants.StreamType.LIVE
          : window.Conviva.Constants.StreamType.VOD;
        const playerInfo = {} as any;
        playerInfo['m3.dv.fw'] = 'Video JS'; // Need to check this tag, and framework already collected
        playerInfo['m3.dv.fwv'] = '7.17.0'; // Need to check this tag, and framework already collected
        let containsHealthspot = false;
        if (assetInfo.aston_segments || assetInfo.video_segments) {
          containsHealthspot = true;
        }
        contentMetadata['c3.cm.containsHealthspot'] = containsHealthspot;
        convivaSDK.setPlayerInfo(playerInfo);
        convivaSDK.reportPlaybackRequested(contentMetadata);
        this.convivaSessionStarted = true;
      }
    },
    // eslint-disable-next-line
    setAdInitialInfo: function () {
      const isConvivaSessionRequired =
        window &&
        (window.isConvivaSessionRequired || window.isConvivaAdSessionRequired);
      if (
        convivaSDK &&
        (!this.convivaSessionStarted || isConvivaSessionRequired)
      ) {
        const contentMetadata = {} as any;
        window.Conviva.Analytics.setDeviceMetadata(getMetaData());
        contentMetadata[window.Conviva.Constants.PLAYER_NAME] =
          convivaCustomConstants.PLAYER_NAME;
        // eslint-disable-next-line
        contentMetadata[window.Conviva.Constants.VIEWER_ID] =
          getLocalStorage(USER_ID) || getLocalStorage(GUEST_TOKEN);
        contentMetadata['c3.viewer.id'] =
          getLocalStorage(USER_ID) || getLocalStorage(GUEST_TOKEN);
        window.convivaAdAnalytics.setAdPlayerInfo(contentMetadata);
        this.convivaSessionStarted = true;
      }
    },
    // eslint-disable-next-line
    reportEvent: function (type: any, payload: any) {
      if (type && convivaSDK && this.convivaSessionStarted) {
        switch (type) {
          case window.Conviva.Constants.PlayerState.PLAYING:
            convivaSDK.reportPlaybackMetric(
              window.Conviva.Constants.Playback.PLAYER_STATE,
              window.Conviva.Constants.PlayerState.PLAYING
            );
            break;
          case 'error':
            if (this.convivaSessionStarted) {
              convivaSDK.reportPlaybackFailed(JSON.stringify(payload), {
                infoMessage: JSON.stringify(payload)
              });
              convivaAdAnalytics.reportAdEnded();
              convivaSDK.reportAdBreakEnded();
              this.convivaSessionStarted = false;
            }
            break;
          case 'vsferror':
            if (this.convivaSessionStarted) {
              const errorPayload =
                typeof payload === 'string' ? payload : JSON.stringify(payload);
              convivaSDK.reportPlaybackFailed(errorPayload);
              convivaAdAnalytics.reportAdEnded();
              convivaSDK.reportAdBreakEnded();
              this.convivaSessionStarted = false;
            }
            break;
          case 'customEvent':
            convivaSDK.reportPlaybackEvent(type, payload);
            break;
          case 'Manifest_Request':
            convivaSDK.reportPlaybackEvent(type, payload);
            break;
          case 'Manifest_Response':
            convivaSDK.reportPlaybackEvent(type, payload);
            break;
          case 'Segment_Init_Request':
            convivaSDK.reportPlaybackEvent(type, payload);
            break;
          case 'Segment_Init_Response':
            convivaSDK.reportPlaybackEvent(type, payload);
            break;
          case 'Segment_Chunk_Request':
            convivaSDK.reportPlaybackEvent(type, payload);
            break;
          case 'Segment_Chunk_Response':
            convivaSDK.reportPlaybackEvent(type, payload);
            break;
          case 'DRM_Licence_Request':
            convivaSDK.reportPlaybackEvent(type, payload);
            break;
          case 'DRM_Session_Acquired':
            convivaSDK.reportPlaybackEvent(type, payload);
            break;
          case 'DRM_Licence_Response':
            convivaSDK.reportPlaybackEvent(type, payload);
            break;
          case 'DRM_Keys_Loaded':
            convivaSDK.reportPlaybackEvent(type, payload);
            break;
          case 'Manifest_Error':
            convivaSDK.reportPlaybackEvent(type, payload);
            break;
          case 'Segment_Init_Error':
            convivaSDK.reportPlaybackEvent(type, payload);
            break;
          case 'Segment_Chunk_Error':
            convivaSDK.reportPlaybackEvent(type, payload);
            break;
          case window.Conviva.Constants.Events.USER_WAIT_STARTED:
            convivaSDK.reportPlaybackEvent(
              window.Conviva.Constants.Events.USER_WAIT_STARTED
            );
            break;
          case 'updateBufferPlayHeadTime':
            if (
              window.videoPlayer &&
              window.videoPlayer.buffered &&
              window.videoPlayer.buffered().length > 0
            ) {
              convivaSDK.reportPlaybackMetric(
                window.Conviva.Constants.Playback.BUFFER_LENGTH,
                window.videoPlayer.buffered().length * 1000
              );
            }
            if (window.videoPlayer && window.videoPlayer.currentTime) {
              convivaSDK.reportPlaybackMetric(
                window.Conviva.Constants.Playback.PLAY_HEAD_TIME,
                window.videoPlayer.currentTime()
              );
            }
            break;
          case 'adBreakStart':
            if (isImadai()) {
              convivaSDK.reportAdBreakStarted(
                window.Conviva.Constants.AdType.SERVER_SIDE,
                window.Conviva.Constants.AdPlayer.CONTENT
              );
            } else {
              convivaSDK.reportAdBreakStarted(
                window.Conviva.Constants.AdType.CLIENT_SIDE,
                window.Conviva.Constants.AdPlayer.SEPARATE
              );
            }
            break;
          case 'adStarted':
            convivaAdAnalytics.reportAdStarted(payload);
            break;
          case 'adCompleted':
            convivaAdAnalytics.reportAdEnded();
            break;
          case 'adSkipped':
            convivaAdAnalytics.reportAdSkipped();
            break;
          case 'adBuffering':
            convivaAdAnalytics.reportAdMetric(
              window.Conviva.Constants.Playback.PLAYER_STATE,
              window.Conviva.Constants.PlayerState.BUFFERING
            );
            break;
          case 'adLoaded':
            convivaAdAnalytics.reportAdLoaded(payload);
            break;
          case 'adError':
            convivaAdAnalytics.reportAdFailed(payload.errorMessage);
            break;
          case 'adPaused':
            convivaAdAnalytics.reportAdMetric(
              window.Conviva.Constants.Playback.PLAYER_STATE,
              window.Conviva.Constants.PlayerState.PAUSED
            );
            break;
          case 'adProgress':
            convivaAdAnalytics.reportAdMetric(
              window.Conviva.Constants.Playback.PLAYER_STATE,
              window.Conviva.Constants.PlayerState.PLAYING
            );
            break;
          case 'adBreakEnd':
            convivaAdAnalytics.reportAdEnded();
            convivaSDK.reportAdBreakEnded();
            break;
          case 'adBitrate':
            if (!(payload === 'N/A')) {
              convivaAdAnalytics.reportAdMetric(
                window.Conviva.Constants.Playback.BITRATE,
                payload
              );
            }
            break;
          case window.Conviva.Constants.Events.USER_WAIT_ENDED:
            convivaSDK.reportPlaybackEvent(
              window.Conviva.Constants.Events.USER_WAIT_ENDED
            );
            break;
          case 'trackChangeEvent':
            break;
          case window.Conviva.Constants.PlayerState.STOPPED:
            // case "playerReset":
            if (this.convivaSessionStarted) {
              convivaSDK.reportPlaybackMetric(
                window.Conviva.Constants.Playback.PLAYER_STATE,
                window.Conviva.Constants.PlayerState.STOPPED
              );
              convivaAdAnalytics.reportAdEnded();
              convivaSDK.reportAdBreakEnded();
              convivaSDK.reportPlaybackEnded();
              this.convivaSessionStarted = false;
            }
            window.isConvivaSessionRequired = false;
            break;
          default:
            break;
        }
      }
    }
  };
};

export default ConvivaAnalyticHelper();

export const pageDidMountConvivaTrigger = (convivaObj: any) => {
  if (window && window.Conviva && convivaObj) {
    convivaObj.reportEvent(window.Conviva.Constants.PlayerState.STOPPED);
    convivaObj.setInitialContentInfo();
  }
};

export const parentalControlVSFTrigger = (
  convivaObj: any,
  errorMessage: any
) => {
  if (
    window &&
    window.Conviva &&
    convivaObj &&
    convivaObj.convivaSessionStarted
  ) {
    convivaObj.reportEvent('vsferror', errorMessage);
  }
};

export const parentalControlConvivaErrorTrigger = (convivaObj: any) => {
  if (window && window.Conviva) {
    if (window && window.parentalPinIncorrect) {
      window.parentalPinIncorrect = undefined;
      parentalControlVSFTrigger(convivaObj, '9007-Incorrect Parental PIN');
      window.parentalPinIncorrect = undefined;
    } else if (window && window.parentalPinPopUpEnabled) {
      window.parentalPinPopUpEnabled = undefined;
      parentalControlVSFTrigger(
        convivaObj,
        '9006-Parental PIN pop up cancelled'
      );
      window.parentalPinPopUpEnabled = undefined;
    } else {
      convivaObj.reportEvent(window.Conviva.Constants.PlayerState.STOPPED);
    }
  }
};

export const getConvivaAdditonalData = (userData: any) => {
  const {
    user: { gender = convivaCustomConstants.NOT_RECEIVED } = {},
    age = convivaCustomConstants.NOT_RECEIVED
  } = userData || {};
  return {
    userAge:
      age && age !== 'NA' && age !== convivaCustomConstants.UNKNOWN
        ? age.toString()
        : convivaCustomConstants.NOT_RECEIVED,
    userGender:
      gender && gender !== 'NA' && gender === convivaCustomConstants.UNKNOWN
        ? convivaCustomConstants.NOT_RECEIVED
        : gender
  };
};

export const createConvivaSessionOnSkip = (playerRef: any) => {
  const { singlePlayDetails = {}, playerConfig = {} } = playerRef || {};

  const {
    assetDetails = {},
    keyOsDetails = {},
    showDetails = {},
    tobaccoAdvisoryDetails = {}
    // watchHistoryDetails: { played_duration = 0 } = {}
  } = singlePlayDetails || {};
  // const convivaAdditonalData = getConvivaAdditonalData(userData);
  const playerAnalyticsObject =
    typeof window !== 'undefined' ? window.playerAnalyticsObject : {};
  if (window && window.convivaAnalytic && window.Conviva) {
    window.convivaAnalytic.reportEvent(
      window.Conviva.Constants.PlayerState.STOPPED
    );
    // assetDetails.videoStartPoint = played_duration !== 0 ? convertSecsToTime(played_duration) : "00:00:00";
    window.isConvivaSessionRequired = true;
    window.convivaAnalytic.setInitialContentInfo(
      {
        ...assetDetails,
        ...keyOsDetails,
        ...tobaccoAdvisoryDetails,
        ...playerAnalyticsObject
      },
      showDetails
    );
  }
};

export function sendSPAPITimeStampToConviva(singlePlayDetails: {
  spapiRequestimeStamp: string;
  spapiResponseTimeStamp: string;
}) {
  // Always Report Conviva ReportPlaybackend for previous content.
  if (window && window.convivaAnalytic && window.convivaAnalytic.reportEvent) {
    const spapiTimeStamp = {
      spapiRequestimeStamp: singlePlayDetails.spapiRequestimeStamp,
      spapiResponseTimeStamp: singlePlayDetails.spapiResponseTimeStamp
    };
    const eventPayload = {
      type: 'SPAPI_TIME_STAMP',
      payload: JSON.stringify(spapiTimeStamp)
    };
    window.convivaAnalytic.reportEvent('customEvent', eventPayload);
  }
}
